import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { mangas, categories } from '@/mocks/mangaData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const sortParam = searchParams.get('sort') || 'latest';

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const categoryName = useMemo(() => {
    const cat = categories.find((c) => c.slug === activeCategory);
    return cat ? cat.name : '全部';
  }, [activeCategory]);

  const filteredMangas = useMemo(() => {
    let result = activeCategory === 'all'
      ? [...mangas]
      : mangas.filter((m) => m.category === activeCategory);

    if (sortParam === 'popular') {
      result.sort((a, b) => parseFloat(b.views.replace('亿', '00000000').replace('万', '0000')) - parseFloat(a.views.replace('亿', '00000000').replace('万', '0000')));
    } else {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return result;
  }, [activeCategory, sortParam]);

  useEffect(() => {
    document.title = `${categoryName}漫画 - 免费下拉式阅读 - 4399漫画网`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `4399漫画网${categoryName}漫画分类，海量免费${categoryName}漫画在线下拉式阅读，极速加载，每日更新。`);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', `4399漫画,下拉式阅读,免费${categoryName}漫画,${categoryName}漫画分类`);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `http://chigua52.cn/category/${activeCategory}`);
  }, [categoryName, activeCategory]);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: `${categoryName}漫画` },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://chigua52.cn/' },
      { '@type': 'ListItem', 'position': 2, 'name': `${categoryName}漫画`, 'item': `http://chigua52.cn/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <header className="relative w-full h-[300px] md:h-[400px] flex flex-col items-center justify-center mb-10 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20illustration%20with%20orange%20coral%20and%20cream%20gradients%2C%20manga%20comic%20categories%20concept%2C%20soft%20geometric%20book%20shelf%20pattern%2C%20minimalist%20design%2C%20warm%20inviting%20tones&width=1800&height=600&seq=category-hero&orientation=landscape"
            alt={`4399漫画网 - ${categoryName}漫画分类`}
            title={`4399漫画网 - ${categoryName}漫画`}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-2xl mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold text-white font-heading">
              {categoryName}漫画
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/70 max-w-lg mx-auto">
              发现最受欢迎的{categoryName}漫画，免费下拉式阅读，极速加载体验
            </p>
          </div>
        </header>

        <main className="px-4 md:px-6 mx-auto max-w-7xl pb-16">
          <div className="flex flex-wrap items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-950">{filteredMangas.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <Link
                to={`/category/${activeCategory}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  sortParam !== 'popular' ? 'bg-white text-foreground-950' : 'text-foreground-500'
                }`}
              >
                最新
              </Link>
              <Link
                to={`/category/${activeCategory}?sort=popular`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  sortParam === 'popular' ? 'bg-white text-foreground-950' : 'text-foreground-500'
                }`}
              >
                最热
              </Link>
            </div>
          </div>

          {filteredMangas.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredMangas.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-400">该分类暂无漫画，看看其他分类吧</p>
              <Link to="/category/all" className="mt-4 inline-block text-primary-500 text-sm">返回全部漫画</Link>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}